import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { PjAgrada } from '@/components/PjAgrada/pjAgradaTypes'
import { PjAgradaDetailSummary } from '@/components/PjAgrada/PjAgradaTable'
import { PJ_AGRADA_STATUS_LABELS, isPlaceholderPhone } from '@/components/PjAgrada/pjAgradaStatus'
import {
  formatCnpj,
  formatCpf,
  formatPhone,
} from '@/components/PjAgrada/pjAgradaFormat'

type Props = {
  prestador: PjAgrada | null
  onClose: () => void
  onEdit?: (prestador: PjAgrada) => void
}

export function PjAgradaDetailModal({ prestador, onClose, onEdit }: Props) {
  useEffect(() => {
    if (prestador) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [prestador])

  if (!prestador) return null

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          className='relative w-full max-w-xl z-10 bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl shadow-2xl overflow-hidden'
        >
          <div className='flex items-start justify-between p-5 border-b border-[var(--glass-border)]'>
            <div>
              <h2 className='font-semibold text-[var(--font-color-h2)]'>{prestador.nomeCompleto}</h2>
              <p className='text-sm text-[var(--font-color-muted)] mt-1'>
                {prestador.areaAtuacao || 'Funcionário PJ'} · {PJ_AGRADA_STATUS_LABELS[prestador.status]}
              </p>
            </div>
            <button type='button' onClick={onClose} className='p-1.5 rounded-md hover:bg-[var(--glass-bg)]'>
              <X size={16} />
            </button>
          </div>

          <div className='p-5 space-y-3 text-sm'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <p className='text-xs uppercase text-[var(--font-color-label)]'>CPF</p>
                <p>{prestador.cpf ? formatCpf(prestador.cpf) : '—'}</p>
              </div>
              <div>
                <p className='text-xs uppercase text-[var(--font-color-label)]'>CNPJ</p>
                <p>{prestador.cnpj ? formatCnpj(prestador.cnpj) : '—'}</p>
              </div>
              <div>
                <p className='text-xs uppercase text-[var(--font-color-label)]'>Telefone</p>
                <p>{isPlaceholderPhone(prestador.telefone) ? '—' : formatPhone(prestador.telefone)}</p>
              </div>
              <div>
                <p className='text-xs uppercase text-[var(--font-color-label)]'>E-mail</p>
                <p>{prestador.email || '—'}</p>
              </div>
            </div>
            <PjAgradaDetailSummary prestador={prestador} />
          </div>

          {onEdit && (
            <div className='p-5 border-t border-[var(--glass-border)] flex justify-end'>
              <Button type='button' variant='primary' size='sm' icon={<Pencil size={14} />} onClick={() => onEdit(prestador)}>
                Editar
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body,
  )
}
